import { Component } from "@theme/component";
import { API_ENDPOINTS } from "@theme/api-endpoints";
import { trackFormSubmit } from "@theme/analytics";

/**
 * <email-unsubscribe> — /pages/emailunsubscribe.
 *
 * Single GET to the unsubscribe-by-email endpoint (no body, no auth) —
 * replaced the old two-call flow (profile lookup by email → PUT an
 * attribute update by the returned shopper_cid, both against
 * devapi2.mashop.com directly with a hardcoded api_key). The endpoint is
 * now App-Proxied like every other external call — see API_ENDPOINTS.
 *
 * On success: hides the form, shows the success message.
 * On error: shows the API error below the form, preserves the entered email.
 */

/** @type {RegExp} Basic email validation */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class EmailUnsubscribe extends Component {
  requiredRefs = ["emailInput", "submitBtn", "errorEl", "successEl", "successText", "formWrap"];

  connectedCallback() {
    super.connectedCallback();

    const prefillEmail = new URLSearchParams(window.location.search).get("email");
    if (prefillEmail) this.refs.emailInput.value = prefillEmail;
  }

  /**
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    return EMAIL_REGEX.test(email);
  }

  /**
   * @param {HTMLElement} errorEl
   * @param {string} message
   */
  #showError(errorEl, message) {
    errorEl.textContent = message;
    errorEl.classList.remove("tw:hidden");
  }

  /**
   * @param {HTMLElement} errorEl
   */
  #hideError(errorEl) {
    errorEl.textContent = "";
    errorEl.classList.add("tw:hidden");
  }

  /**
   * Wire up Shopify advanced captcha if present, then run the callback.
   * @param {HTMLFormElement} formEl
   * @param {() => void} callback
   */
  #withCaptcha(formEl, callback) {
    if (window.Shopify?.recaptchaV3?.execute) {
      window.Shopify.recaptchaV3.execute(formEl, callback);
    } else {
      callback();
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = async event => {
    event.preventDefault();

    const formEl = event.target.closest("form");
    const { emailInput, submitBtn, errorEl, successEl, successText, formWrap } = this.refs;

    const email = emailInput.value.trim();

    if (!email) {
      this.#showError(errorEl, this.dataset.validationEmailRequired || "Email is required.");
      return;
    }
    if (!this.#isValidEmail(email)) {
      this.#showError(errorEl, this.dataset.validationEmailInvalid || "Please enter a valid email address.");
      return;
    }

    submitBtn.disabled = true;
    this.#hideError(errorEl);

    this.#withCaptcha(formEl, () =>
      this.#submit(email, { formEl, submitBtn, errorEl, successEl, successText, formWrap })
    );
  };

  /**
   * GET the unsubscribe endpoint for the given email and update the UI.
   * @param {string} email
   * @param {Object} ui — { formEl, submitBtn, errorEl, successEl, successText, formWrap }
   */
  async #submit(email, ui) {
    const { formEl, submitBtn, errorEl, successEl, successText, formWrap } = ui;

    try {
      const response = await fetch(`${API_ENDPOINTS.customerUnsubscribeEmail}/${encodeURIComponent(email)}`);

      if (response.ok) {
        trackFormSubmit(formEl);

        formWrap.classList.add("tw:hidden");
        successText.textContent = this.dataset.successMessage || "You've been unsubscribed. We're sorry to see you go!";
        successEl.classList.remove("tw:hidden");
        successEl.classList.add("tw:flex");
      } else {
        this.#showError(errorEl, this.dataset.errorMessage || "Something went wrong. Please try again.");
        submitBtn.disabled = false;
      }
    } catch {
      this.#showError(errorEl, this.dataset.errorMessage || "Something went wrong. Please try again.");
      submitBtn.disabled = false;
    }
  }
}

if (!customElements.get("email-unsubscribe")) {
  customElements.define("email-unsubscribe", EmailUnsubscribe);
}
